import { useState, useEffect, useCallback } from 'react';
import { checkoutFormsAPI, productsAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { Plus, Trash2, Code, Copy, MoreVertical, Pencil, Loader2, ShoppingBag, User, Mail, Phone, MapPin, FileText, Eye, CreditCard, Lock, ShieldCheck, Check, Info } from 'lucide-react';
import { Alert, AlertDescription } from '../components/ui/alert';
import PlanLimitBanner from '../components/PlanLimitBanner';

const AVAILABLE_FIELDS = [
  { key: 'name', label: 'Full Name', icon: User, required: true },
  { key: 'email', label: 'Email', icon: Mail, required: true },
  { key: 'phone', label: 'Phone Number', icon: Phone, required: false },
  { key: 'address', label: 'Address', icon: MapPin, required: false },
  { key: 'notes', label: 'Order Notes', icon: FileText, required: false },
];

export default function CheckoutFormsPage() {
  const { user } = useAuth();
  const [forms, setForms] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editForm, setEditForm] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [embedDialogOpen, setEmbedDialogOpen] = useState(false);
  const [embedForm, setEmbedForm] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const [formsRes, productsRes] = await Promise.all([
        checkoutFormsAPI.list(),
        productsAPI.list()
      ]);
      setForms(formsRes.data);
      setProducts(productsRes.data);
    } catch (err) {
      toast.error('Failed to load data');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const openCreate = () => {
    setEditForm({
      name: 'My Checkout Form',
      product_ids: [],
      fields: ['name', 'email'],
      button_text: 'Buy Now',
      button_color: '#195259',
      show_product_selector: true,
      payment_methods: ['fpx', 'card', 'ewallet'],
      success_message: 'Thank you! Check your email for the download link.',
      show_branding: true,
    });
    setDialogOpen(true);
  };

  const openEdit = (form) => {
    setEditForm({ ...form });
    setDialogOpen(true);
  };

  const toggleProduct = (productId) => {
    setEditForm(prev => {
      const ids = prev.product_ids || [];
      return { ...prev, product_ids: ids.includes(productId) ? ids.filter(i => i !== productId) : [...ids, productId] };
    });
  };

  const toggleField = (fieldKey) => {
    const required = AVAILABLE_FIELDS.find(f => f.key === fieldKey)?.required;
    if (required) return; // Can't remove required fields
    setEditForm(prev => {
      const fields = prev.fields || [];
      return { ...prev, fields: fields.includes(fieldKey) ? fields.filter(f => f !== fieldKey) : [...fields, fieldKey] };
    });
  };

  const handleSave = async () => {
    if (!editForm.name?.trim()) { toast.error('Form name is required'); return; }
    if (!editForm.product_ids?.length) { toast.error('Select at least one product'); return; }
    setSaving(true);
    try {
      if (editForm.id) {
        await checkoutFormsAPI.update(editForm.id, editForm);
        toast.success('Form updated');
      } else {
        await checkoutFormsAPI.create(editForm);
        toast.success('Form created');
      }
      setDialogOpen(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this checkout form?')) return;
    try {
      await checkoutFormsAPI.delete(id);
      toast.success('Form deleted');
      loadData();
    } catch { toast.error('Delete failed'); }
  };

  const showEmbed = (form) => {
    setEmbedForm(form);
    setEmbedDialogOpen(true);
  };

  const getEmbedCode = (form) => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    return `<!-- Funnely Checkout: ${form.name} -->
<div id="funnely-checkout-${form.id.slice(0,8)}"></div>
<script src="${backendUrl}/api/embed/js/${form.id}"></script>`;
  };

  const copyEmbed = (form) => {
    navigator.clipboard.writeText(getEmbedCode(form));
    toast.success('Embed code copied!');
  };

  if (loading) {
    return <div className="space-y-4"><div className="h-10 w-48 bg-muted animate-pulse rounded" /><div className="h-64 bg-muted animate-pulse rounded-xl" /></div>;
  }

  return (
    <div className="space-y-6 max-w-[1200px]">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Checkout Forms</h1>
          <p className="text-sm text-muted-foreground mt-1">Create embeddable checkout forms for your external websites</p>
        </div>
        <Button onClick={openCreate} data-testid="checkout-forms-create-button">
          <Plus className="h-4 w-4 mr-1.5" /> New Form
        </Button>
      </div>

      <PlanLimitBanner resource="checkout_forms" label="checkout forms" />

      <Card className="border-primary/20 bg-accent/30">
        <CardContent className="pt-5 pb-5">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-primary shrink-0 mt-0.5" />
            <div className="space-y-2">
              <p className="text-sm font-semibold">What happens after a customer purchases?</p>
              <ol className="text-xs text-muted-foreground space-y-1.5 list-decimal list-inside leading-relaxed">
                <li>Customer fills in the checkout form and completes payment via your configured payment gateway.</li>
                <li>After successful payment, customer is redirected to a <strong className="text-foreground">Thank You page</strong> with a unique, personal download link.</li>
                <li>The download link has a <strong className="text-foreground">maximum download limit</strong> and <strong className="text-foreground">expiry time</strong> based on your product settings.</li>
                <li>Customer must <strong className="text-foreground">save/bookmark the Thank You page link</strong> — this is their only way to access the download.</li>
                <li>You can view all orders, reissue download links, or revoke access from the <strong className="text-foreground">Orders</strong> page in your dashboard.</li>
              </ol>
              <p className="text-[10px] text-muted-foreground italic">Tip: If no payment gateway is configured, purchases will be processed in test mode (auto-approved).</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {forms.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Code className="h-12 w-12 text-muted-foreground/30 mb-4" />
            <h3 className="text-lg font-semibold">No checkout forms yet</h3>
            <p className="text-sm text-muted-foreground mt-1 mb-4">Create a form, select products, and get an embed code for your website</p>
            <Button onClick={openCreate}><Plus className="h-4 w-4 mr-1.5" /> Create Form</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {forms.map(form => (
            <Card key={form.id} className="card-hover">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-semibold">{form.name}</h3>
                    <p className="text-xs text-muted-foreground mt-1">{(form.product_ids || []).length} product{(form.product_ids || []).length !== 1 ? 's' : ''} &middot; {(form.fields || []).length} fields</p>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8"><MoreVertical className="h-4 w-4" /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openEdit(form)}><Pencil className="h-4 w-4 mr-2" /> Edit</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleDelete(form.id)} className="text-destructive"><Trash2 className="h-4 w-4 mr-2" /> Delete</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  {(form.fields || []).map(f => <Badge key={f} variant="secondary" className="text-[10px]">{f}</Badge>)}
                </div>

                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="flex-1" onClick={() => openEdit(form)}>
                    <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                  </Button>
                  <Button size="sm" variant="outline" className="flex-1" onClick={() => showEmbed(form)}>
                    <Eye className="h-3.5 w-3.5 mr-1" /> View
                  </Button>
                  <Button size="sm" className="flex-1" onClick={() => copyEmbed(form)}>
                    <Code className="h-3.5 w-3.5 mr-1" /> Get Code
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editForm?.id ? 'Edit Checkout Form' : 'Create Checkout Form'}</DialogTitle>
          </DialogHeader>
          {editForm && (
            <div className="space-y-5">
              <div className="space-y-2">
                <Label>Form Name</Label>
                <Input value={editForm.name} onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))} placeholder="My Checkout Form" />
              </div>

              {/* Product Selection */}
              <div className="space-y-2">
                <Label>Products</Label>
                <p className="text-xs text-muted-foreground">Select which products customers can buy through this form</p>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {products.filter(p => p.is_published).map(p => (
                    <div key={p.id} className={`flex items-center gap-3 p-2.5 rounded-lg border transition-colors ${(editForm.product_ids || []).includes(p.id) ? 'bg-accent border-primary/20' : 'hover:bg-muted/50'}`}>
                      <Checkbox checked={(editForm.product_ids || []).includes(p.id)} onCheckedChange={() => toggleProduct(p.id)} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{p.name}</p>
                        <p className="text-xs text-muted-foreground">RM {parseFloat(p.price || 0).toFixed(2)}</p>
                      </div>
                    </div>
                  ))}
                  {products.filter(p => p.is_published).length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No published products</p>}
                </div>
              </div>

              <Separator />

              {/* Customer Fields */}
              <div className="space-y-2">
                <Label>Customer Details to Collect</Label>
                <p className="text-xs text-muted-foreground">Choose what information to collect from customers</p>
                <div className="space-y-2">
                  {AVAILABLE_FIELDS.map(field => {
                    const Icon = field.icon;
                    const isActive = (editForm.fields || []).includes(field.key);
                    return (
                      <div key={field.key} className={`flex items-center gap-3 p-2.5 rounded-lg border transition-colors ${isActive ? 'bg-accent border-primary/20' : ''}`}>
                        <Checkbox checked={isActive} onCheckedChange={() => toggleField(field.key)} disabled={field.required} />
                        <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
                        <div className="flex-1">
                          <p className="text-sm font-medium">{field.label}</p>
                        </div>
                        {field.required && <Badge variant="secondary" className="text-[10px]">Required</Badge>}
                      </div>
                    );
                  })}
                </div>
              </div>

              <Separator />

              {/* Customization */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Button Text</Label>
                  <Input value={editForm.button_text || 'Buy Now'} onChange={e => setEditForm(p => ({ ...p, button_text: e.target.value }))} />
                </div>
                <div className="space-y-2">
                  <Label>Button Color</Label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={editForm.button_color || '#195259'} onChange={e => setEditForm(p => ({ ...p, button_color: e.target.value }))} className="w-10 h-10 rounded border cursor-pointer" />
                    <span className="text-xs font-mono text-muted-foreground">{editForm.button_color}</span>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Success Message</Label>
                <Input value={editForm.success_message || ''} onChange={e => setEditForm(p => ({ ...p, success_message: e.target.value }))} placeholder="Thank you! Check your email..." />
              </div>

              <div className="flex items-center justify-between">
                <Label>Show Product Selector</Label>
                <Switch checked={editForm.show_product_selector !== false} onCheckedChange={v => setEditForm(p => ({ ...p, show_product_selector: v }))} />
              </div>

              <Separator />

              {/* Payment Methods — now auto-derived from creator's configured gateways */}
              <div className="space-y-2">
                <Label>Payment Method</Label>
                <div className="p-3 rounded-lg border bg-muted/40 text-xs text-muted-foreground flex items-start gap-2">
                  <Info className="h-4 w-4 mt-0.5 shrink-0" style={{ color: '#195259' }} />
                  <span>
                    Customers will see the payment gateway you&apos;ve connected (Billplz, ToyyibPay, SenangPay, or Stripe). To change this, go to <strong>Payment Settings</strong>.
                  </span>
                </div>
              </div>

              <Separator />

              {/* Funnely Branding */}
              {/* Branding removed from plan features */}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}
              {editForm?.id ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View/Embed Dialog */}
      <Dialog open={embedDialogOpen} onOpenChange={setEmbedDialogOpen}>
        <DialogContent className="max-w-lg p-0 overflow-hidden">
          <div className="px-6 pt-6 pb-3 border-b">
            <DialogTitle className="text-lg">{embedForm?.name || 'Checkout Form'}</DialogTitle>
            <p className="text-sm text-muted-foreground mt-1">Preview of your checkout form</p>
          </div>
          {embedForm && (
            <div className="overflow-y-auto max-h-[70vh] px-6 py-4">
              {(() => {
                const selectedProducts = products.filter(p => (embedForm.product_ids || []).includes(p.id));
                const totalAmount = selectedProducts.reduce((sum, p) => sum + (p.price || 0), 0);
                const btnColor = embedForm.button_color || '#195259';
                return (
                  <div>
                    {/* Product Selection */}
                    {selectedProducts.length > 0 && (
                      <div className="space-y-3 mb-5">
                        <h2 className="text-base font-semibold">Choose your products</h2>
                        <p className="text-sm text-muted-foreground">Select one or more products below, then complete checkout</p>
                        {selectedProducts.map(product => (
                          <div key={product.id} className="rounded-xl border-2 p-3.5" style={{ borderColor: btnColor, backgroundColor: `${btnColor}08` }}>
                            <div className="flex items-start gap-3">
                              <div className="h-5 w-5 rounded-full border-2 flex items-center justify-center shrink-0 mt-0.5" style={{ borderColor: btnColor, backgroundColor: btnColor }}>
                                <Check className="h-3 w-3 text-white" />
                              </div>
                              {product.thumbnail_url ? (
                                <img src={product.thumbnail_url} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0" />
                              ) : (
                                <div className="w-12 h-12 rounded-lg shrink-0 flex items-center justify-center" style={{ backgroundColor: `${btnColor}15` }}>
                                  <ShoppingBag className="h-4 w-4" style={{ color: btnColor, opacity: 0.4 }} />
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <h3 className="font-semibold text-sm">{product.name}</h3>
                                <Badge variant="secondary" className="mt-0.5 text-[10px]">{product.type}</Badge>
                                {product.description && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{product.description}</p>}
                              </div>
                              <span className="text-base font-bold shrink-0" style={{ color: btnColor }}>RM {parseFloat(product.price || 0).toFixed(2)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Checkout Section */}
                    <div className="rounded-2xl border-2 overflow-hidden" style={{ borderColor: `${btnColor}30` }}>
                      <div className="p-4" style={{ backgroundColor: `${btnColor}08` }}>
                        <h2 className="text-base font-semibold">Checkout</h2>
                        <div className="mt-2 space-y-1">
                          {selectedProducts.map(p => (
                            <div key={p.id} className="flex items-center justify-between text-sm">
                              <span>{p.name}</span>
                              <span className="font-mono" style={{ color: btnColor }}>RM {parseFloat(p.price || 0).toFixed(2)}</span>
                            </div>
                          ))}
                          <Separator className="my-2" />
                          <div className="flex items-center justify-between font-semibold">
                            <span>Total</span>
                            <span className="text-lg" style={{ color: btnColor }}>RM {totalAmount.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>

                      <div className="p-4 space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          {(embedForm.fields || []).filter(f => f === 'name' || f === 'email').map(f => (
                            <div key={f} className="space-y-1.5">
                              <label className="text-sm font-medium block">{f === 'name' ? 'Full Name' : 'Email'}</label>
                              <Input disabled placeholder={f === 'name' ? 'Your name' : 'you@example.com'} className="h-9" />
                            </div>
                          ))}
                        </div>
                        {(embedForm.fields || []).filter(f => f !== 'name' && f !== 'email').map(f => (
                          <div key={f} className="space-y-1.5">
                            <label className="text-sm font-medium block capitalize">{f === 'phone' ? 'Phone Number' : f}</label>
                            <Input disabled placeholder={f === 'phone' ? '+60 12-345 6789' : `Enter ${f}`} className="h-9" />
                          </div>
                        ))}
                        <div className="space-y-1.5">
                          <label className="text-sm font-medium block">Payment Method</label>
                          <div className="h-9 rounded-md border flex items-center px-3 text-sm text-muted-foreground bg-muted/20" data-testid="checkout-preview-gateway">
                            Configured gateway (live)
                          </div>
                        </div>
                        <div className="flex items-center gap-2 p-2.5 rounded-lg text-xs bg-muted/30">
                          <Lock className="h-3.5 w-3.5 shrink-0 opacity-60" />
                          <span className="opacity-70">Secure checkout. You'll get instant download access after payment.</span>
                        </div>
                        <Button className="w-full" size="lg" style={{ backgroundColor: btnColor }} disabled>
                          <ShieldCheck className="h-4 w-4 mr-2" /> Pay RM {totalAmount.toFixed(2)}
                        </Button>
                      </div>
                    </div>
                    {(user?.plan !== 'premium' || embedForm?.show_branding !== false) && (
                      <div className="flex items-center justify-center gap-1 mt-3">
                        <span className="text-[10px] text-muted-foreground">Powered by</span>
                        <img src="/logos/funnely-teal.png" alt="Funnely" className="h-2.5 object-contain" />
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}
          <div className="px-6 py-3 border-t flex justify-between">
            <Button variant="outline" onClick={() => setEmbedDialogOpen(false)}>Close</Button>
            <Button onClick={() => embedForm && copyEmbed(embedForm)}>
              <Code className="h-4 w-4 mr-1.5" /> Copy Embed Code
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
